/** RaiderMapCR - Current courses page with search and filters */
import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { 
  Schedule as ScheduleIcon, 
  Room as RoomIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Person as PersonIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SchedulePage = () => {
  const [allClasses, setAllClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [starredClasses, setStarredClasses] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    modality: '',
    course_prefix: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/schedules')
      .then(response => response.json())
      .then(data => {
        setAllClasses(data);
        setFilteredClasses(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching class catalog:', error);
        setLoading(false);
      });
    const saved = localStorage.getItem('starredClasses');
    if (saved) {
      setStarredClasses(new Set(JSON.parse(saved)));
    }
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
    }
  }, []);

  useEffect(() => {
    let filtered = allClasses.filter(classItem => {
      // Search term filtering
      const courseName = (classItem.course_name || '').toString().toLowerCase();
      const courseCode = ((classItem.course_prefix || '') + ' ' + (classItem.course_number || '')).toString().toLowerCase();
      const professor = (classItem.professor || '').toString().toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = courseName.includes(searchLower) || 
                           courseCode.includes(searchLower) || 
                           professor.includes(searchLower);
      const matchesType = !filters.type || 
                         (classItem.type && classItem.type.toLowerCase() === filters.type.toLowerCase());
      const matchesModality = !filters.modality || 
                             (classItem.modality && classItem.modality.toLowerCase() === filters.modality.toLowerCase());
      const matchesPrefix = !filters.course_prefix || 
                           (classItem.course_prefix && classItem.course_prefix.toLowerCase() === filters.course_prefix.toLowerCase());

      return matchesSearch && matchesType && matchesModality && matchesPrefix;
    });
    if (activeTab === 1) {
      filtered = filtered.filter(classItem => starredClasses.has(classItem.id));
    }

    setFilteredClasses(filtered);
  }, [searchTerm, allClasses, starredClasses, activeTab, filters]);

  const toggleStar = (classId) => {
    const newStarred = new Set(starredClasses);
    if (newStarred.has(classId)) {
      newStarred.delete(classId);
    } else {
      newStarred.add(classId);
    }
    setStarredClasses(newStarred);
    localStorage.setItem('starredClasses', JSON.stringify([...newStarred]));
  };

  const handleProfessorClick = (professorName) => {
    if (professorName) {
      navigate(`/directory?search=${encodeURIComponent(professorName)}`);
    }
  };

  const handleRoomClick = (roomName) => {
    if (roomName) {
      // Navigate to map page with room name as search parameter
      navigate(`/map?room=${encodeURIComponent(roomName)}`);
    }
  };

  const handleFilterClick = useCallback(() => {
    if (filterPanelOpen) {
      flushSync(() => {
        setIsAnimating(true);
        setFilterPanelOpen(false);
      });
      setTimeout(() => {
        setIsAnimating(false);
      }, 200);
    } else {
      flushSync(() => {
        setIsAnimating(true);
        setFilterPanelOpen(true);
      });
    }
  }, [filterPanelOpen]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const getUniqueTypes = () => {
    return [...new Set(allClasses.map(classItem => classItem.type).filter(Boolean))];
  };

  const getUniqueModalities = () => {
    return [...new Set(allClasses.map(classItem => classItem.modality).filter(Boolean))];
  };

  const getUniquePrefixes = () => {
    return [...new Set(allClasses.map(classItem => classItem.course_prefix).filter(Boolean))];
  };

  const distributeItemsEvenly = (items, numColumns) => {
    const result = [];
    const totalItems = items.length;
    const itemsPerColumn = Math.floor(totalItems / numColumns);
    const extraItems = totalItems % numColumns;
    
    let startIndex = 0;
    for (let i = 0; i < numColumns; i++) {
      let itemsInThisColumn = itemsPerColumn;
      if (i === 0) {
        itemsInThisColumn = Math.max(0, itemsPerColumn);
      } else {
        const adjustedExtraItems = extraItems + 1;
        itemsInThisColumn += (i - 1 < adjustedExtraItems ? 1 : 0);
      }
      
      const endIndex = startIndex + itemsInThisColumn;
      result.push(items.slice(startIndex, endIndex));
      startIndex = endIndex;
    }
    return result;
  };

  const getResponsivePrefixDistribution = () => {
    const prefixes = getUniquePrefixes();
    const mobileColumns = 4;
    const desktopColumns = 6;
    
    return {
      mobile: distributeItemsEvenly(prefixes, mobileColumns),
      desktop: distributeItemsEvenly(prefixes, desktopColumns)
    };
  };

  const formatRoomLabel = (room) => {
    if (!room) return '';
    const trimmedRoom = String(room).trim();
    if (/^\d+$/.test(trimmedRoom)) {
      return `Room ${trimmedRoom}`;
    }
    return trimmedRoom;
  };

  if (loading) {
    return (
      <div className="main-content">
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <ScheduleIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#ffffff' }}>
            Current Courses
          </Typography>
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Click on professors or rooms to get more information
          </Typography>
        </Box>

        <Box sx={{ mb: 3, position: 'relative', zIndex: 10 }}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Name, code, or professor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#ffffff' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleFilterClick}
                        sx={{ 
                          color: '#ffffff',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        <FilterListIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              sx={{ 
                mb: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: (filterPanelOpen || isAnimating) ? '12px 12px 0 0' : 3,
                  backgroundColor: 'rgba(42, 42, 42, 0.85)',
                  border: '1px solid rgba(74, 74, 74, 0.6)',
                  backdropFilter: 'blur(12px)',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                  transition: 'border-radius 150ms ease-in-out',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: 'primary.main',
                    },
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: (filterPanelOpen || isAnimating) ? '12px 12px 0 0' : 3,
                  borderWidth: '1px',
                  height: '68px',
                  top: '-12px',
                  border: 'none',
                  transition: 'border-radius 150ms ease-in-out',
                },
                '& .MuiInputBase-input': {
                  padding: '16.5px 14px',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                },
                '& .MuiInputAdornment-root': {
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                },
              }}
            />
            <Box sx={{ 
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 5,
              overflow: 'hidden',
              maxHeight: filterPanelOpen ? '600px' : '0px',
              transition: 'max-height 300ms ease-in-out',
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
            }}>
              <Box sx={{ 
                p: 3,
                backgroundColor: 'rgba(42, 42, 42, 0.85)',
                border: '1px solid rgba(74, 74, 74, 0.6)',
                borderTop: 'none',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                backdropFilter: 'blur(12px)',
              }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <FormLabel 
                          component="legend" 
                          sx={{ 
                            color: '#ffffff !important', 
                            fontWeight: 'bold',
                            mb: 1,
                            fontSize: '0.8rem',
                            pointerEvents: 'none',
                            cursor: 'default',
                            '&.Mui-focused': {
                              color: '#ffffff !important'
                            }
                          }}
                        >
                          Type
                        </FormLabel>
                        <RadioGroup
                          value={filters.type}
                          onChange={(e) => handleFilterChange('type', e.target.value)}
                          sx={{ 
                            '& .MuiFormControlLabel-label': {
                              color: '#ffffff',
                              fontSize: '0.75rem'
                            },
                            '& .MuiRadio-root': {
                              color: 'rgba(255, 255, 255, 0.7)',
                              padding: '2px',
                              '&.Mui-checked': {
                                color: '#AA3333'
                              }
                            }
                          }}
                        >
                          <FormControlLabel 
                            value="" 
                            control={<Radio size="small" />} 
                            label="All" 
                          />
                          {getUniqueTypes().map(type => (
                            <FormControlLabel 
                              key={type}
                              value={type} 
                              control={<Radio size="small" />} 
                              label={type} 
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <FormLabel 
                          component="legend" 
                          sx={{ 
                            color: '#ffffff !important', 
                            fontWeight: 'bold',
                            mb: 1,
                            fontSize: '0.8rem',
                            pointerEvents: 'none',
                            cursor: 'default',
                            '&.Mui-focused': {
                              color: '#ffffff !important'
                            }
                          }}
                        >
                          Modality
                        </FormLabel>
                        <RadioGroup
                          value={filters.modality}
                          onChange={(e) => handleFilterChange('modality', e.target.value)}
                          sx={{ 
                            '& .MuiFormControlLabel-label': {
                              color: '#ffffff',
                              fontSize: '0.75rem'
                            },
                            '& .MuiRadio-root': {
                              color: 'rgba(255, 255, 255, 0.7)',
                              padding: '2px',
                              '&.Mui-checked': {
                                color: '#AA3333'
                              }
                            }
                          }}
                        >
                          <FormControlLabel 
                            value="" 
                            control={<Radio size="small" />} 
                            label="All" 
                          />
                          {getUniqueModalities().map(modality => (
                            <FormControlLabel 
                              key={modality}
                              value={modality} 
                              control={<Radio size="small" />} 
                              label={modality} 
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel 
                      component="legend" 
                      sx={{ 
                        color: '#ffffff !important', 
                        fontWeight: 'bold',
                        mb: 1,
                        fontSize: '0.8rem',
                        pointerEvents: 'none',
                        cursor: 'default',
                        '&.Mui-focused': {
                          color: '#ffffff !important'
                        }
                      }}
                    >
                      Course Prefix
                    </FormLabel>
                    <Grid container spacing={1}>
                      {getResponsivePrefixDistribution().mobile.map((columnPrefixes, columnIndex) => (
                        <Grid item xs={3} md={0} key={`mobile-${columnIndex}`} sx={{ display: { xs: 'block', md: 'none' } }}>
                          <RadioGroup
                            value={filters.course_prefix}
                            onChange={(e) => handleFilterChange('course_prefix', e.target.value)}
                            sx={{ 
                              '& .MuiFormControlLabel-label': {
                                color: '#ffffff',
                                fontSize: '0.75rem'
                              },
                              '& .MuiRadio-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                padding: '2px',
                                '&.Mui-checked': {
                                  color: '#AA3333'
                                }
                              }
                            }}
                          >
                            {columnIndex === 0 && (
                              <FormControlLabel 
                                value="" 
                                control={<Radio size="small" />} 
                                label="All" 
                              />
                            )}
                            {columnPrefixes.map(prefix => (
                              <FormControlLabel 
                                key={prefix}
                                value={prefix} 
                                control={<Radio size="small" />} 
                                label={prefix} 
                              />
                            ))}
                          </RadioGroup>
                        </Grid>
                      ))}
                      {getResponsivePrefixDistribution().desktop.map((columnPrefixes, columnIndex) => (
                        <Grid item xs={0} md={2} key={`desktop-${columnIndex}`} sx={{ display: { xs: 'none', md: 'block' } }}>
                          <RadioGroup
                            value={filters.course_prefix}
                            onChange={(e) => handleFilterChange('course_prefix', e.target.value)}
                            sx={{ 
                              '& .MuiFormControlLabel-label': {
                                color: '#ffffff',
                                fontSize: '0.75rem'
                              },
                              '& .MuiRadio-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                padding: '2px',
                                '&.Mui-checked': {
                                  color: '#AA3333'
                                }
                              }
                            }}
                          >
                            {columnIndex === 0 && (
                              <FormControlLabel 
                                value="" 
                                control={<Radio size="small" />} 
                                label="All" 
                              />
                            )}
                            {columnPrefixes.map(prefix => (
                              <FormControlLabel 
                                key={prefix}
                                value={prefix} 
                                control={<Radio size="small" />} 
                                label={prefix} 
                              />
                            ))}
                          </RadioGroup>
                        </Grid>
                      ))}
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>

              </Box>
            </Box>
          </Box>
        </Box>

        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'rgba(74, 74, 74, 0.6)',
            '& .MuiTab-root': {
              color: '#ffffff',
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
            },
          }}
        >
          <Tab label="All Classes" />
          <Tab 
            label={`Starred Classes (${starredClasses.size})`}
            icon={<StarIcon />}
            iconPosition="start"
          />
        </Tabs>

        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
          {filteredClasses.length === 0 ? (
            <Grid item xs={12}>
              <Card sx={{
                backgroundColor: 'rgba(42, 42, 42, 0.85)',
                border: '1px solid rgba(74, 74, 74, 0.6)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    {searchTerm ? 'No classes found for your search.' : 
                     activeTab === 1 ? 'No starred classes yet.' : 
                     'No classes available.'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: '#ffffff' }}>
                    {searchTerm ? 'Try adjusting your search terms.' : 
                     activeTab === 1 ? 'Star some classes to see them here!' : 
                     'Class catalog will be updated soon.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            filteredClasses.map((classItem, index) => (
              <Grid item xs={12} md={6} key={classItem.id || index}>
                <Card sx={{ 
                  height: '100%',
                  backgroundColor: 'rgba(42, 42, 42, 0.85)',
                  border: starredClasses.has(classItem.id) ? '2px solid #AA3333' : '1px solid rgba(74, 74, 74, 0.6)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  position: 'relative',
                  display: 'flex',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  }
                }}>
                  <CardContent sx={{ 
                    pb: 2, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexGrow: 1
                  }}>
                    <Box sx={{ position: 'absolute', top: '50%', right: 2, zIndex: 1, transform: 'translateY(-50%)' }}>
                      <IconButton
                        onClick={() => toggleStar(classItem.id)}
                        sx={{ 
                          color: starredClasses.has(classItem.id) ? '#AA3333' : '#666666',
                          '&:hover': { color: '#AA3333' },
                          borderRadius: '50%',
                          padding: '6px',
                          '& .MuiSvgIcon-root': {
                            filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                            borderRadius: '2px'
                          }
                        }}
                      >
                        {starredClasses.has(classItem.id) ? <StarIcon fontSize="large" /> : <StarBorderIcon fontSize="large" />}
                      </IconButton>
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      textAlign: 'center'
                    }}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 0.5, color: '#ffffff' }}>
                          {classItem.course_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ffffff' }}>
                          {classItem.course_prefix} {classItem.course_number}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip 
                          label={classItem.time} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderColor: 'rgba(74, 74, 74, 0.6)',
                            color: '#ffffff'
                          }}
                        />
                        <Chip 
                          label={classItem.days} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderColor: 'rgba(74, 74, 74, 0.6)',
                            color: '#ffffff'
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        {classItem.type && (
                          <Chip 
                            label={classItem.type}
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              borderColor: 
                                classItem.type.toLowerCase() === 'lecture' ? '#1e3a8a' :
                                classItem.type.toLowerCase() === 'discussion' ? '#7c3aed' :
                                classItem.type.toLowerCase() === 'lab' ? '#ea580c' :
                                '#d1d5db',
                              color: 
                                classItem.type.toLowerCase() === 'lecture' ? '#1e3a8a' :
                                classItem.type.toLowerCase() === 'discussion' ? '#7c3aed' :
                                classItem.type.toLowerCase() === 'lab' ? '#ea580c' :
                                '#6b7280'
                            }}
                          />
                        )}
                        {classItem.modality && (
                          <Chip 
                            label={classItem.modality}
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              borderColor: 
                                classItem.modality.toLowerCase() === 'face to face' ? '#7c2d12' :
                                classItem.modality.toLowerCase() === 'videoconference' ? '#166534' :
                                '#d1d5db',
                              color: 
                                classItem.modality.toLowerCase() === 'face to face' ? '#7c2d12' :
                                classItem.modality.toLowerCase() === 'videoconference' ? '#166534' :
                                '#6b7280'
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        {classItem.modality !== 'videoconference' && (
                          <Chip
                            icon={<RoomIcon sx={{ fontSize: 16 }} />}
                            label={formatRoomLabel(classItem.room)}
                            size="small"
                            variant="outlined"
                            clickable={!!classItem.room}
                            onClick={() => handleRoomClick(classItem.room)}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              borderColor: 'rgba(74, 74, 74, 0.6)',
                              color: '#ffffff',
                              backgroundColor: classItem.room ? 'rgba(42, 42, 42, 0.6)' : 'rgba(42, 42, 42, 0.3)',
                              cursor: classItem.room ? 'pointer' : 'default',
                              '&:hover': classItem.room ? {
                                backgroundColor: 'rgba(209, 0, 0, 0.2)',
                                borderColor: '#AA3333',
                                color: '#ffffff'
                              } : {},
                              '& .MuiChip-icon': {
                                color: classItem.room ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                              }
                            }}
                          />
                        )}
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: 16 }} />}
                          label={classItem.professor}
                          size="small"
                          variant="outlined"
                          clickable={!!classItem.professor}
                          onClick={() => handleProfessorClick(classItem.professor)}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderColor: 'rgba(74, 74, 74, 0.6)',
                            color: '#ffffff',
                            backgroundColor: classItem.professor ? 'rgba(42, 42, 42, 0.6)' : 'rgba(42, 42, 42, 0.3)',
                            cursor: classItem.professor ? 'pointer' : 'default',
                            '&:hover': classItem.professor ? {
                              backgroundColor: 'rgba(209, 0, 0, 0.2)',
                              borderColor: '#AA3333',
                              color: '#ffffff'
                            } : {},
                            '& .MuiChip-icon': {
                              color: classItem.professor ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
          </Grid>
        </Box>
      </Container>
    </div>
  );
};

export default SchedulePage;
